/**
 * Constructor autosuggest overlay for the search page input.
 * When the user types in the search bar on /search, shows an overlay with
 * suggestions. Mobile: white overlay; desktop: dropdown (4 suggestions).
 */

import { debounce } from '@theme/utilities';
import { buildAutocompleteOptions, normalizeAutocompleteResponse } from '@theme/predictive-search-constructor-data';
import { escapeHtml, waitForConstructorClient } from '@theme/constructor-utils';

const DESKTOP_SUGGESTIONS_LIMIT = 4;
const SUGGEST_DEBOUNCE_MS = 300;
const BLUR_HIDE_DELAY_MS = 200;
const RECENT_SEARCH_STORAGE_KEY = 'cnstrc-predictive-search-recent';
const RECENT_SEARCH_MAX = 5;

/**
 * @param {string} baseSearchUrl
 * @param {string} query
 * @returns {string}
 */
function buildSearchUrl(baseSearchUrl, query) {
  const searchUrl = new URL(baseSearchUrl, window.location.origin);
  searchUrl.searchParams.set('type', 'product');
  searchUrl.searchParams.set('q', query);
  return searchUrl.toString();
}

/**
 * @param {unknown} response - Raw Constructor autocomplete response
 * @param {string} currentQuery - Current input value
 * @param {string} baseSearchUrl
 * @returns {{ text: string, url: string }[]}
 */
function formatSuggestions(response, currentQuery, baseSearchUrl) {
  const parsed = normalizeAutocompleteResponse(response, currentQuery, 0, (query) =>
    buildSearchUrl(baseSearchUrl, query),
  );

  /** @type {Set<string>} */
  const seen = new Set();

  return parsed.suggestions.reduce(
    /**
     * @param {{ text: string, url: string }[]} acc
     * @param {{ text: string, url: string }} suggestion
     */
    (acc, suggestion) => {
      const text = String(suggestion?.text ?? '').trim();
      if (!text) return acc;

      const dedupeKey = text.toLowerCase();
      if (seen.has(dedupeKey)) return acc;
      seen.add(dedupeKey);

      acc.push({
        text,
        url: suggestion?.url || buildSearchUrl(baseSearchUrl, text),
      });
      return acc;
    },
    [],
  );
}

function isDesktop() {
  return typeof window !== 'undefined' && window.matchMedia('(min-width: 1080px)').matches;
}

/**
 * @returns {string[]}
 */
function getRecentSearches() {
  try {
    const raw = localStorage.getItem(RECENT_SEARCH_STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];

    const seen = new Set();
    return parsed
      .filter((item) => typeof item === 'string')
      .map((item) => item.trim())
      .filter((item) => {
        if (!item || seen.has(item)) return false;
        seen.add(item);
        return true;
      })
      .slice(0, RECENT_SEARCH_MAX);
  } catch {
    return [];
  }
}

/**
 * @param {string[]} terms
 */
function setRecentSearches(terms) {
  try {
    localStorage.setItem(RECENT_SEARCH_STORAGE_KEY, JSON.stringify(terms));
  } catch {
    // localStorage may be unavailable
  }
}

/**
 * @param {string} term
 */
function addToRecentSearches(term) {
  const trimmed = term.trim();
  if (!trimmed) return;

  const current = getRecentSearches();
  const next = [trimmed, ...current.filter((item) => item !== trimmed)].slice(0, RECENT_SEARCH_MAX);
  setRecentSearches(next);
}

/**
 * Highlight query match in text (same as predictive search).
 * Escapes text first, then wraps matching query in <strong>.
 * @param {string} text
 * @param {string} query
 * @returns {string} HTML string
 */
function highlightMatch(text, query) {
  const escapedText = escapeHtml(text);
  if (!query || !text) return escapedText;
  const escapedQuery = escapeHtml(query);
  if (!escapedQuery) return escapedText;
  const regex = new RegExp(`(${escapedQuery.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
  return escapedText.replace(regex, '<strong>$1</strong>');
}

function init() {
  const overlay = document.querySelector('.search-page-input__overlay');
  if (!overlay || !(overlay instanceof HTMLElement)) return;

  const inputId = overlay.getAttribute('data-search-input-id');
  const input = inputId
    ? document.getElementById(inputId)
    : overlay.closest('form')?.querySelector('input[name="q"]');
  if (!input || !(input instanceof HTMLInputElement)) return;

  const list = overlay.querySelector(
    '[data-cnstrc-suggestions-list], [data-cnstrc-autosuggest]',
  );
  if (!list || !(list instanceof HTMLElement)) return;

  const searchUrl = typeof Theme !== 'undefined' && Theme?.routes?.search_url
    ? Theme.routes.search_url
    : '/search';
  const windowWithConstructorLoader = /** @type {Window & { ConstructorioSdkLoader?: { injectSdk?: () => void } }} */ (
    window
  );
  const constructorSdkLoader = windowWithConstructorLoader.ConstructorioSdkLoader;

  /** @type {ReturnType<typeof setTimeout> | null} */
  let hideTimeout = null;
  /** @type {AbortController | null} */
  let searchAbortController = null;

  /** @type {HTMLElement} */
  const overlayEl = overlay;
  /** @type {HTMLInputElement} */
  const inputEl = input;
  /** @type {HTMLElement} */
  const listEl = list;

  function createSearchAbortController() {
    if (searchAbortController) {
      searchAbortController.abort();
    }
    searchAbortController = new AbortController();
    return searchAbortController;
  }

  function showOverlay() {
    overlayEl.hidden = false;
    inputEl.setAttribute('aria-expanded', 'true');
    if (!isDesktop()) {
      document.documentElement.setAttribute('scroll-lock', '');
    }
  }

  function hideOverlay() {
    overlayEl.hidden = true;
    inputEl.setAttribute('aria-expanded', 'false');
    if (!isDesktop()) {
      document.documentElement.removeAttribute('scroll-lock');
    }
  }

  function renderSuggestions(/** @type {{ text: string, url: string }[]} */ items) {
    const limit = isDesktop() ? DESKTOP_SUGGESTIONS_LIMIT : items.length;
    const toShow = items.slice(0, limit);

    if (toShow.length === 0) {
      hideOverlay();
      return;
    }

    const query = inputEl.value.trim();
    listEl.innerHTML = toShow
      .map(
        (s, index) =>
          `<li
            class="cnstrc-predictive-search__suggestion-item"
            role="option"
            data-query="${escapeHtml(s.text)}"
            data-index="${index}"
          >
            <a href="${escapeHtml(s.url)}" class="cnstrc-predictive-search__suggestion-link" data-cnstrc-item-section="Search Suggestions" data-cnstrc-item-name="${escapeHtml(s.text)}" data-cnstrc-item-id="${escapeHtml(s.text)}" data-suggestion>${highlightMatch(s.text, query)}</a>
          </li>`,
      )
      .join('');

    listEl.querySelectorAll('.cnstrc-predictive-search__suggestion-link').forEach((link) => {
      link.addEventListener('click', (e) => {
        e.preventDefault();
        const suggestionItem = link.closest('.cnstrc-predictive-search__suggestion-item');
        const query = suggestionItem?.getAttribute('data-query')?.trim() || '';
        addToRecentSearches(query);
        const href = link.getAttribute('href');
        if (href) window.location.href = href;
      });
    });

    showOverlay();
  }

  const fetchSuggestions = debounce(async () => {
    const query = inputEl.value.trim();
    if (!query) {
      if (searchAbortController) {
        searchAbortController.abort();
        searchAbortController = null;
      }
      hideOverlay();
      return;
    }

    const signal = createSearchAbortController().signal;

    try {
      constructorSdkLoader?.injectSdk?.();

      const client = await waitForConstructorClient({
        signal,
        predicate: (candidate) => Boolean(candidate?.autocomplete?.getAutocompleteResults),
      });
      if (!client?.autocomplete?.getAutocompleteResults || signal.aborted) {
        hideOverlay();
        return;
      }

      const response = await client.autocomplete.getAutocompleteResults(
        query,
        buildAutocompleteOptions(0),
      );
      if (signal.aborted || query !== inputEl.value.trim()) return;

      const suggestions = formatSuggestions(response, query, searchUrl);
      renderSuggestions(suggestions);
    } catch (err) {
      if (err?.name !== 'AbortError') {
        console.error('[Constructor] Search page suggest error:', err);
      }
      if (signal.aborted) return;
      hideOverlay();
    }
  }, SUGGEST_DEBOUNCE_MS);

  inputEl.addEventListener('input', () => {
    if (!inputEl.value.trim()) {
      hideOverlay();
      return;
    }
    fetchSuggestions();
  });

  inputEl.addEventListener('focus', () => {
    if (inputEl.value.trim() && listEl.children.length > 0) showOverlay();
  });

  inputEl.addEventListener('blur', () => {
    hideTimeout = window.setTimeout(() => {
      hideOverlay();
      hideTimeout = null;
    }, BLUR_HIDE_DELAY_MS);
  });

  inputEl.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') {
      hideOverlay();
      inputEl.blur();
    }
  });

  overlayEl.addEventListener('mousedown', (_e) => {
    if (hideTimeout) {
      clearTimeout(hideTimeout);
      hideTimeout = null;
    }
  });

  document.addEventListener('click', (e) => {
    if (overlayEl.hidden) return;
    if (!overlayEl.contains(/** @type {Node} */ (e.target)) && e.target !== inputEl) {
      hideOverlay();
    }
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}
